import {
  Language,
  SALUTATIONS,
  LETTER_TITLES,
  PARAGRAPH_2,
  PARAGRAPH_3,
  PARAGRAPH_5,
  CLOSING_FORMULAS,
  ATTENDANCE_CODE_LABELS,
  ATTENDANCE_TYPE_LABELS,
} from "./types";

interface LetterPreviewProps {
  language: Language;
  firstName: string;
  lastName: string;
  outletName: string;
  gender: string;
  commsEmail: string;
  signedByName: string;
  signedByTitle: string;
  eventName?: string;
  eventDates?: string;
  eventVenue?: string;
  attendanceCode?: string;
  attendanceType?: "physical" | "online";
}

const MONTHS: Record<Language, string[]> = {
  fr: ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"],
  en: ["January","February","March","April","May","June","July","August","September","October","November","December"],
  pt: ["janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"],
};

function formatDate(lang: Language): string {
  const now = new Date();
  const day = now.getDate();
  const month = MONTHS[lang][now.getMonth()];
  const year = now.getFullYear();
  if (lang === "fr") return `Abuja, le ${day} ${month} ${year}`;
  return `Abuja, ${day} ${month} ${year}`;
}

export function LetterPreview({
  language,
  firstName,
  lastName,
  outletName,
  gender,
  commsEmail,
  signedByName,
  signedByTitle,
  eventName,
  eventDates,
  eventVenue,
  attendanceCode,
  attendanceType,
}: LetterPreviewProps) {
  const salutation =
    SALUTATIONS[language][gender] ||
    (language === "fr" ? "Cher(e)" : language === "en" ? "Dear" : "Caro(a)");

  const hasEvent = !!(eventName && eventDates && eventVenue);

  const paragraph1: React.ReactNode = (() => {
    if (language === "fr") {
      return hasEvent ? (
        <>
          Le Parlement de la CEDEAO a le plaisir de vous convier pour la couverture
          médiatique de <strong>{eventName}</strong> prévue du{" "}
          <strong>{eventDates}</strong> à <strong>{eventVenue}</strong>.
        </>
      ) : (
        "Le Parlement de la CEDEAO a le plaisir de vous convier pour la couverture médiatique de ses activités."
      );
    }
    if (language === "en") {
      return hasEvent ? (
        <>
          The ECOWAS Parliament is pleased to invite you to provide media coverage of{" "}
          <strong>{eventName}</strong> scheduled from <strong>{eventDates}</strong> at{" "}
          <strong>{eventVenue}</strong>.
        </>
      ) : (
        "The ECOWAS Parliament is pleased to invite you to provide media coverage of its activities."
      );
    }
    return hasEvent ? (
      <>
        O Parlamento da CEDEAO tem o prazer de convidá-lo(a) para a cobertura mediática
        de <strong>{eventName}</strong> prevista de <strong>{eventDates}</strong> em{" "}
        <strong>{eventVenue}</strong>.
      </>
    ) : (
      "O Parlamento da CEDEAO tem o prazer de convidá-lo(a) para a cobertura mediática das suas actividades."
    );
  })();

  const paragraph4 = (() => {
    const emailSpan = <strong>{commsEmail}</strong>;
    if (language === "fr")
      return (
        <>
          Dans l&apos;espoir que vous honorerez la présente invitation, nous vous
          saurons gré de confirmer dans les meilleurs délais votre participation à
          l&apos;adresse suivante : {emailSpan}, afin que toutes les dispositions
          relatives à votre voyage (billets d&apos;avion) soient prises dans
          l&apos;immédiat.
        </>
      );
    if (language === "en")
      return (
        <>
          We hope you will honour this invitation and kindly request that you confirm
          your participation as soon as possible at the following address: {emailSpan},
          so that all travel arrangements (flight tickets) can be made immediately.
        </>
      );
    return (
      <>
        Na esperança de que honre o presente convite, agradecemos que confirme com a
        maior brevidade possível a sua participação no seguinte endereço: {emailSpan},
        para que todas as disposições relativas à sua viagem (passagens aéreas) sejam
        tomadas de imediato.
      </>
    );
  })();

  const closingFormula = CLOSING_FORMULAS[language].replace(
    "{salutation}",
    salutation
  );

  return (
    <div
      className="bg-white text-[11pt] leading-relaxed font-serif"
      style={{ padding: "72pt", minHeight: "792pt", maxWidth: "612pt", margin: "0 auto" }}
    >
      {/* 72pt top margin is blank, letterhead space */}
      <div style={{ height: "72pt" }} />

      {/* Journalist block */}
      <div className="mb-6">
        <p className="text-justify font-bold">
          {firstName} {lastName}
        </p>
        {outletName && <p className="text-justify font-bold">{outletName}</p>}
      </div>

      {/* Date, right aligned */}
      <div className="text-right mb-8">{formatDate(language)}</div>

      {/* Salutation */}
      <p className="text-justify mb-4">{salutation},</p>

      {/* Title */}
      <p className="text-justify font-bold text-[12pt] mb-4">{LETTER_TITLES[language]}</p>

      {/* Paragraph 1 */}
      <p className="text-justify hyphens-auto mb-4">{paragraph1}</p>

      {/* Paragraph 2 */}
      <p className="text-justify hyphens-auto mb-4">{PARAGRAPH_2[language]}</p>

      {/* Paragraph 3 */}
      <p className="text-justify hyphens-auto mb-4">{PARAGRAPH_3[language]}</p>

      {/* Paragraph 4 */}
      <p className="text-justify hyphens-auto mb-4">{paragraph4}</p>

      {/* Attendance code box (Phase 4 data, optional) */}
      {attendanceCode && attendanceType && (
        <div
          className="mb-4 rounded border border-gray-300 bg-gray-50 p-2"
          style={{ padding: "8pt" }}
        >
          <span className="font-bold">{ATTENDANCE_CODE_LABELS[language]}</span>{" "}
          <span
            className="font-mono font-bold text-[12pt]"
            style={{ color: "#006B3C" }}
          >
            {attendanceCode}
          </span>{" "}
          <span className="text-muted-foreground text-[10pt]">
            {ATTENDANCE_TYPE_LABELS[language][attendanceType]}
          </span>
        </div>
      )}

      {/* Paragraph 5 */}
      <p className="text-justify hyphens-auto mb-8">{PARAGRAPH_5[language]}</p>

      {/* Closing */}
      <p className="text-justify mb-16">{closingFormula}</p>

      {/* Signatory block, right-aligned */}
      <div className="text-right">
        <p className="text-justify font-bold">{signedByName}</p>
        <p>{signedByTitle}</p>
      </div>
    </div>
  );
}
